import React, { forwardRef, useId } from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helperText?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  /** Decorative icon (non-interactive). Prefer `rightElement` for buttons. */
  rightIcon?: React.ReactNode;
  /** Interactive trailing control (e.g. password reveal). Takes precedence over `rightIcon`. */
  rightElement?: React.ReactNode;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      leftIcon,
      rightIcon,
      rightElement,
      containerClassName,
      className = '',
      id,
      disabled,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;
    const hasRight = Boolean(rightElement || rightIcon);

    // Strip trailing asterisk if present in label string to avoid double asterisks when required is set
    const cleanLabel = label?.replace(/\s*\*\s*$/, '');

    return (
      <div className={containerClassName || 'w-full'}>
        {cleanLabel && (
          <label htmlFor={inputId} className="mb-1 block text-xs font-semibold text-neutral-700">
            {cleanLabel}
            {props.required && <span className="ml-1 text-red-500">*</span>}
          </label>
        )}
        <div className="relative rounded-lg shadow-2xs">
          {leftIcon && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-neutral-400">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            aria-invalid={!!error}
            aria-describedby={error ? errorId : helperText ? helperId : undefined}
            className={`block h-10 w-full rounded-lg border px-3 text-sm transition-colors hover:border-neutral-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)] disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-500 ${
              leftIcon ? 'pl-9' : ''
            } ${hasRight ? 'pr-10' : ''} ${
              error
                ? 'border-red-300 text-red-900 placeholder-red-300 focus-visible:outline-red-500'
                : 'border-neutral-300 text-neutral-900 placeholder-neutral-400'
            } ${className}`}
            {...props}
          />
          {rightElement ? (
            <div className="absolute inset-y-0 right-0 flex items-center pr-1">{rightElement}</div>
          ) : rightIcon ? (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400">
              {rightIcon}
            </div>
          ) : null}
        </div>
        {error && (
          <p id={errorId} className="mt-1 text-xs font-medium text-red-600">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p id={helperId} className="mt-1 text-xs text-neutral-500">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
